/**
 * Site Diary — Client PO income tile for the selected project (admin + surveyor).
 * Shows the PO total and count; tapping lists the POs with a link to view each document.
 */
import { useEffect, useState } from 'react';
import { ClipboardList, ChevronDown, ChevronUp, FileText, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { InvoiceViewer } from '../../components/cost-inbox/InvoiceViewer';
import { PAPER } from './theme';

const fmtGbp = (n) => (n == null ? '—' : `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`);
const fmtD = (s) => (s ? new Date(s).toLocaleDateString('en-GB') : '');

export function ClientPoTile({ projectId, refreshKey }) {
  const { api, isAdmin, isSurveyor } = useAuth();
  const canSee = isAdmin || isSurveyor;
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    if (!canSee || !projectId) return undefined;
    let cancelled = false;
    api.get(`/client-pos/project/${projectId}`).then(r => { if (!cancelled) setData(r.data); }).catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [api, projectId, canSee, refreshKey]);

  if (!canSee || !data || !data.count) return null;

  return (
    <div className="rounded-xl mt-2.5 overflow-hidden" style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, boxShadow: PAPER.shadow }} data-testid="client-po-tile">
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full px-3 py-3 flex items-center gap-3 text-left" data-testid="client-po-tile-toggle">
        <ClipboardList className="w-4 h-4 shrink-0" style={{ color: PAPER.accent }} />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: PAPER.muted }}>Client PO income</div>
          <div className="text-[15px] font-bold tabular-nums" style={{ color: PAPER.accent }} data-testid="client-po-tile-total">{fmtGbp(data.total_pounds)} <span className="text-[11px] font-normal" style={{ color: PAPER.hint }}>ex VAT · {data.count} PO{data.count === 1 ? '' : 's'}</span></div>
        </div>
        {open ? <ChevronUp className="w-4 h-4" style={{ color: PAPER.inkSoft }} /> : <ChevronDown className="w-4 h-4" style={{ color: PAPER.inkSoft }} />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-1.5" data-testid="client-po-tile-list">
          {data.items.map(po => (
            <div key={po.id} className="flex items-center gap-2 text-xs rounded-lg px-2.5 py-2" style={{ background: 'rgba(0,0,0,0.03)', border: `1px solid ${PAPER.divider}` }} data-testid={`client-po-tile-row-${po.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate" style={{ color: PAPER.ink }}>{po.po_number || 'PO'} <span className="font-normal" style={{ color: PAPER.muted }}>· {po.client_name || ''}</span></div>
                <div style={{ color: PAPER.hint }}>{fmtD(po.po_date)}{po.description ? ` · ${po.description}` : ''}{po.status === 'closed' ? ' · closed' : ''}</div>
              </div>
              <div className="font-bold tabular-nums" style={{ color: PAPER.accent }}>{fmtGbp(po.value_pounds)}</div>
              <button type="button" onClick={() => setViewing(po)} className="p-1 rounded hover:bg-black/5" title="View the PO" data-testid={`client-po-tile-view-${po.id}`}><FileText className="w-4 h-4" style={{ color: PAPER.inkSoft }} /></button>
            </div>
          ))}
        </div>
      )}
      {viewing && (
        <div className="fixed inset-0 z-[80] bg-[#1E2530] text-white flex flex-col" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }} data-testid="client-po-tile-viewer">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10 shrink-0">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{viewing.po_number || 'PO'} · {fmtGbp(viewing.value_pounds)} <span className="text-[11px] font-normal text-gray-400">ex VAT</span></div>
              <div className="text-[11px] text-gray-400 truncate">{viewing.client_name || ''}{viewing.description ? ` · ${viewing.description}` : ''}</div>
            </div>
            <button type="button" onClick={() => setViewing(null)} className="p-2 rounded-full hover:bg-white/10" aria-label="Close" data-testid="client-po-tile-viewer-close"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 min-h-0">
            <InvoiceViewer api={api} itemId={viewing.id} pageCount={viewing.page_count || 1} filename={viewing.original_filename || 'po'} base="/client-pos" />
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientPoTile;
